import { inject, Injector } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { catchError, forkJoin, map, of } from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { FleetValidationByType } from '../../../../common/interfaces/validation/fleet-validation';
import { ValidationService } from '../../../../common/services/validation.service';
import type { OrderFormResolverData } from '../../orders.routes';
import { OrdersRoute } from '../enums/orders-route';
import { OrderFormHelper } from '../helpers/order-form.helper';
import { OrderHelper } from '../helpers/order.helper';
import type { Order } from '../interfaces/order';
import { OrdersService } from '../services/orders.service';

export const editOrderResolver: ResolveFn<
  OrderFormResolverData & {
    fleetValidation: FleetValidationByType;
    order: Order;
  }
> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const ordersService = inject(OrdersService);
  const orderFormHelper = inject(OrderFormHelper);
  const validationService = inject(ValidationService);
  const toastService = inject(OnyxToastService);
  const injector = inject(Injector);

  const uuid = route.paramMap.get('uuid');
  const urlTree = router.parseUrl(OrdersRoute.ORDERS_LIST);

  if (!uuid) {
    return new RedirectCommand(urlTree, { replaceUrl: true });
  }

  return forkJoin([
    ordersService.getOrder(uuid),
    validationService.getFleetValidationByType(),
  ]).pipe(
    map(([dto, fleetValidation]) => {
      if (!OrderHelper.hasEdit(dto)) {
        toastService.showError({
          message: 'toasts.archived',
          keepOnNavigation: true,
        });
        return new RedirectCommand(urlTree, { replaceUrl: true });
      }

      const editLock = OrderHelper.getEditLock(dto);
      if (editLock.disabled) {
        toastService.showError({
          message: editLock.tooltip,
          keepOnNavigation: true,
        });
        return new RedirectCommand(urlTree, { replaceUrl: true });
      }

      const defaultDispatch = orderFormHelper.getDefaultDispatch();
      return {
        fleetValidation,
        order: dto,
        form: OrderFormHelper.fromDto(dto, defaultDispatch),
        initial: OrderFormHelper.fromDto(dto, defaultDispatch),
      };
    }),
    catchError((response: OnyxHttpErrorResponse) => {
      if (VALIDATION_ERROR_CODES.includes(response.status)) {
        toastService.showError({
          message: 'orders.toasts.notFound',
          keepOnNavigation: true,
        });
        response.handled = true;
      } else {
        ValidationHelper.handleUnexpectedResponse(response, injector, {
          keepOnNavigation: true,
        });
      }

      return of(new RedirectCommand(urlTree, { replaceUrl: true }));
    }),
  );
};
